import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Card, SectionTitle, ProgressBar } from "../components/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, HardHat } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import EstimacionesObra from "../components/EstimacionesObra";
import ControlObra from "../components/ControlObra";

export default function Construccion() {
  const { pid } = useParams();
  const { canEdit, isAdmin } = useAuth();
  const [avances, setAvances] = useState(null);
  const [creditos, setCreditos] = useState([]);
  const [upes, setUpes] = useState([]);
  const [f, setF] = useState({ fecha: new Date().toISOString().slice(0, 10) });

  const load = () => api.get(`/properties/${pid}/avances`).then((r) => setAvances(r.data));
  useEffect(() => {
    load();
    api.get(`/properties/${pid}/creditos`).then((r) => setCreditos(r.data));
    api.get(`/properties/${pid}/upes`).then((r) => setUpes(r.data));
  }, [pid]); // eslint-disable-line

  const guardar = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${pid}/avances`, {
        fecha: f.fecha, avance_pct: Number(f.avance_pct) || 0, descripcion: f.descripcion || "",
        upe_id: f.upe_id || null, credito_id: f.credito_id || null,
      });
      toast.success(f.credito_id ? "Avance certificado registrado y aplicado a la disposición del crédito" : "Avance certificado registrado");
      setF({ fecha: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const borrar = async (a) => {
    if (!window.confirm("¿Eliminar este avance certificado?")) return;
    try { await api.delete(`/avances/${a.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const ultimo = avances?.[0];
  const upeName = (id) => upes.find((u) => u.id === id)?.clave || "—";
  const credName = (id) => { const c = creditos.find((x) => x.id === id); return c ? `${c.tipo} · ${c.acreditante_nombre}` : "—"; };

  return (
    <div className="p-4 sm:p-8 max-w-[1100px]">
      <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Módulo Construcción</p>
      <h1 className="font-display text-3xl font-bold text-[#002B49] mb-5">Control de obra</h1>

      <Tabs defaultValue="avances">
        <TabsList className="mb-4">
          <TabsTrigger value="avances" data-testid="tab-avances">Avances certificados</TabsTrigger>
          <TabsTrigger value="estimaciones" data-testid="tab-estimaciones">Estimaciones (IA)</TabsTrigger>
          <TabsTrigger value="control" data-testid="tab-control">Control de obra</TabsTrigger>
        </TabsList>

        <TabsContent value="control">
          <ControlObra pid={pid} />
        </TabsContent>

        <TabsContent value="estimaciones">
          <EstimacionesObra pid={pid} />
        </TabsContent>

        <TabsContent value="avances">
      <Card className="p-5 mb-5" testId="avance-actual">
        <div className="flex items-center gap-4">
          <HardHat size={28} className="text-[#00A3B5]" />
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600 font-semibold">Avance físico certificado</span>
              <span className="font-display font-bold text-[#002B49]" data-testid="avance-fisico-pct">{ultimo ? `${ultimo.avance_pct}%` : "Sin certificados"}</span>
            </div>
            <ProgressBar value={ultimo?.avance_pct || 0} />
            {ultimo && <p className="text-xs text-slate-400 mt-1">Último certificado: {fmtDate(ultimo.fecha)} por {ultimo.certificado_por}</p>}
          </div>
        </div>
      </Card>

      {avances && avances.length > 1 && (
        <Card className="p-5 mb-5" testId="curva-avance">
          <SectionTitle>Curva de avance físico certificado</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={[...avances].reverse().map((a) => ({ fecha: fmtDate(a.fecha), pct: a.avance_pct }))}>
              <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748B" }} unit="%" width={40} />
              <RTooltip formatter={(v) => [`${v}%`, "Avance"]} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Line type="monotone" dataKey="pct" stroke="#00A3B5" strokeWidth={2.5} dot={{ r: 3, fill: "#002B49" }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {canEdit && (
        <Card className="p-5 mb-5">
          <SectionTitle>Registrar avance certificado del supervisor</SectionTitle>
          <form onSubmit={guardar} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="space-y-1.5"><Label>Fecha *</Label>
              <Input data-testid="avance-fecha-input" type="date" required value={f.fecha || ""} onChange={(e) => setF({ ...f, fecha: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>% avance *</Label>
              <Input data-testid="avance-pct-input" type="number" min="0" max="100" step="0.1" required value={f.avance_pct || ""} onChange={(e) => setF({ ...f, avance_pct: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>UPE (opcional)</Label>
              <Select value={f.upe_id || "todas"} onValueChange={(v) => setF({ ...f, upe_id: v === "todas" ? null : v })}>
                <SelectTrigger data-testid="avance-upe-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="todas">Todo el proyecto</SelectItem>
                  {upes.map((u) => <SelectItem key={u.id} value={u.id}>{u.clave}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5 md:col-span-2"><Label>Alimentar disposición del crédito (opcional)</Label>
              <Select value={f.credito_id || "ninguno"} onValueChange={(v) => setF({ ...f, credito_id: v === "ninguno" ? null : v })}>
                <SelectTrigger data-testid="avance-credito-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ninguno">Sin crédito</SelectItem>
                  {creditos.map((c) => <SelectItem key={c.id} value={c.id}>{c.tipo} · {c.acreditante_nombre}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button data-testid="avance-guardar-button" type="submit" className="h-10 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><Plus size={14} /> Registrar</Button>
            <div className="space-y-1.5 md:col-span-6"><Label>Descripción / certificado del supervisor</Label>
              <Input value={f.descripcion || ""} onChange={(e) => setF({ ...f, descripcion: e.target.value })} placeholder="Ej. Certificación mensual de obra — estructura nivel 3 concluida" /></div>
          </form>
          <p className="text-[11px] text-slate-400 mt-2">Si vinculas un crédito, el % se aplica como avance certificado en su disposición más reciente.</p>
        </Card>
      )}

      <Card className="overflow-hidden" testId="tabla-avances">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">% avance</th><th className="px-4 py-3">Alcance</th>
              <th className="px-4 py-3">Crédito alimentado</th><th className="px-4 py-3">Descripción</th>
              <th className="px-4 py-3">Certificó</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {avances?.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sin avances certificados. Registra el primer certificado del supervisor de obra.</td></tr>}
              {(avances || []).map((a) => (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`avance-row-${a.id}`}>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(a.fecha)}</td>
                  <td className="px-4 py-3 font-bold text-[#002B49]">{a.avance_pct}%</td>
                  <td className="px-4 py-3 text-slate-600">{a.upe_id ? upeName(a.upe_id) : "Todo el proyecto"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{a.credito_id ? credName(a.credito_id) : "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-[220px] truncate" title={a.descripcion}>{a.descripcion || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{a.certificado_por}</td>
                  <td className="px-4 py-3 text-right">{isAdmin && <button onClick={() => borrar(a)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}</td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
